import { useEffect, useState } from "react";
import { appCRUD } from "../../../actions/app";
import { TableShared } from "../../../shared";
import { char } from "../../../utils";
import { Button, Dropdown, Space, Tag, Modal, Form, Input, InputNumber, Select, Switch, Row, Col, Divider, message } from "antd";
import { MoreOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useNotification } from "../../../utils/notificationProvider";

const { TextArea } = Input;
const { Option } = Select;

const ROOM_TYPES = [
  { value: "LECTURE", label: "Lecture Hall" },
  { value: "LAB", label: "Laboratory" },
  { value: "TUTORIAL", label: "Tutorial Room" },
  { value: "SEMINAR", label: "Seminar Room" },
  { value: "COMPUTER_LAB", label: "Computer Lab" },
  { value: "AUDITORIUM", label: "Auditorium" },
  { value: "CONFERENCE", label: "Conference Room" },
  { value: "WORKSHOP", label: "Workshop" },
  { value: "STUDIO", label: "Studio" },
  { value: "LIBRARY", label: "Library" },
];

export const RoomContent = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [postsPerPage, setePostPerPage] = useState(10);
  const [nextPage, setNextPage] = useState(0);
  const [value, setValue] = useState("");
  const [index, setIndex] = useState(0);
  const [sortColumn, setSortColumn] = useState("");
  const [sortType, setSortType] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [selectedCampus, setSelectedCampus] = useState(null);
  const openNotification = useNotification();
  const [form] = Form.useForm();

  // Load campuses
  useEffect(() => {
    appCRUD.loadCampas(100, 0, "", "name")
      .then((resp) => setCampuses(resp.data.results || []))
      .catch(console.error);

    appCRUD.loadVenueTypeActive()
      .then((resp) => setVenueTypes(resp.data || []))
      .catch(console.error);
  }, []);

  // Load buildings when campus changes
  useEffect(() => {
    if (selectedCampus) {
      appCRUD.loadBuildingByCampus(selectedCampus)
        .then((resp) => setBuildings(resp.data || []))
        .catch(console.error);
    } else {
      setBuildings([]);
    }
  }, [selectedCampus]);

  // Load rooms
  useEffect(() => {
    setLoading(true);
    appCRUD
      .loadRoom(postsPerPage, nextPage, value, "code")
      .then((resp) => {
        setData(resp.data.results);
        setCount(resp.data.count);
      })
      .catch((error) => {
        openNotification("error", "Error", error.message);
      })
      .finally(() => setLoading(false));
  }, [page, postsPerPage, value, index, nextPage]);

  const handleCampusChange = (campusId) => {
    setSelectedCampus(campusId);
    form.setFieldsValue({ building: undefined });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const submitData = {
        ...values,
        cumpas: values.campus,
      };
      delete submitData.campus;

      if (editData) {
        await appCRUD.updateRoom(editData.id, submitData);
        message.success("Room updated successfully");
      } else {
        await appCRUD.addRoom(submitData);
        message.success("Room created successfully");
      }
      setModalOpen(false);
      setEditData(null);
      form.resetFields();
      setSelectedCampus(null);
      setIndex(index + 1);
    } catch (error) {
      message.error(error.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditData(record);
    const campusId = record.cumpas?.id;
    setSelectedCampus(campusId);

    // Load buildings for the campus first
    if (campusId) {
      appCRUD.loadBuildingByCampus(campusId)
        .then((resp) => {
          setBuildings(resp.data || []);
          form.setFieldsValue({
            campus: campusId,
            building: record.building?.id,
            venue_type: record.venue_type?.id,
            code: record.code,
            name: record.name,
            room_type: record.room_type,
            floor: record.floor,
            minimum: record.minimum,
            maximum: record.maximum,
            exam_capacity: record.exam_capacity,
            has_projector: record.has_projector,
            has_whiteboard: record.has_whiteboard,
            has_smartboard: record.has_smartboard,
            has_air_conditioning: record.has_air_conditioning,
            has_computers: record.has_computers,
            computer_count: record.computer_count,
            has_video_conferencing: record.has_video_conferencing,
            has_audio_system: record.has_audio_system,
            wheelchair_accessible: record.wheelchair_accessible,
            is_active: record.is_active,
            is_available_for_booking: record.is_available_for_booking,
            notes: record.notes,
          });
        });
    }
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Delete Room",
      content: "Are you sure you want to delete this room?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await appCRUD.deleteRoom(id);
          message.success("Room deleted successfully");
          setIndex(index + 1);
        } catch (error) {
          message.error("Failed to delete room");
        }
      }
    });
  };

  const defaultColumns = [
    { key: "sn", title: "#", dataIndex: "sn", fixed: true, width: 50 },
    { key: "code", title: "Code", dataIndex: "code", sortable: true, width: 100 },
    { key: "name", title: "Room Name", dataIndex: "name", sortable: true, width: 180 },
    { key: "building", title: "Building", dataIndex: "building", width: 120 },
    { key: "floor", title: "Floor", dataIndex: "floor", width: 60 },
    { key: "type", title: "Type", dataIndex: "type", width: 100 },
    { key: "capacity", title: "Capacity", dataIndex: "capacity", width: 80 },
    { key: "facilities", title: "Facilities", dataIndex: "facilities", width: 150 },
    { key: "status", title: "Status", dataIndex: "status", width: 80 },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 80,
      render: (record) => (
        <Dropdown
          menu={{
            items: [
              { label: "Edit", key: "edit", icon: <EditOutlined /> },
              { label: "Delete", key: "delete", icon: <DeleteOutlined />, danger: true },
            ],
            onClick: (event) => {
              if (event.key === "edit") handleEdit(record);
              if (event.key === "delete") handleDelete(record.id);
            },
          }}
          placement="bottomRight"
        >
          <Button type="dashed" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  const dataItem = data?.map((dt, i) => ({
    ...dt,
    sn: i + 1 + nextPage,
    code: dt.code?.toUpperCase(),
    name: dt.name ? char.toTitleCase(dt.name) : "N/A",
    building: dt.building?.name ? char.toTitleCase(dt.building.name) : "N/A",
    floor: dt.floor === 0 ? "Ground" : dt.floor < 0 ? `B${Math.abs(dt.floor)}` : dt.floor,
    type: ROOM_TYPES.find(t => t.value === dt.room_type)?.label || dt.room_type,
    capacity: `${dt.minimum || 0} - ${dt.maximum || 0}`,
    facilities: (
      <Space size={[0, 4]} wrap>
        {dt.has_projector && <Tag color="blue">Projector</Tag>}
        {dt.has_computers && <Tag color="green">Computers</Tag>}
        {dt.has_air_conditioning && <Tag color="cyan">AC</Tag>}
      </Space>
    ),
    status: dt.is_active ? <Tag color="success">Active</Tag> : <Tag color="error">Inactive</Tag>,
  }));

  const button = (
    <Button type="primary" icon={<PlusOutlined />} onClick={() => {
      setEditData(null);
      form.resetFields();
      setSelectedCampus(null);
      setModalOpen(true);
    }}>
      New Room
    </Button>
  );

  return (
    <>
      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col-12">
            <p className="h6 fs-18 fw-bold text-dark">Rooms / Venues</p>
          </div>
          <hr className="mt-3" />
        </div>
        <TableShared
          loading={loading}
          data={dataItem}
          setData={setData}
          page={page}
          setPage={setPage}
          count={count}
          postsPerPage={postsPerPage}
          setePostPerPage={setePostPerPage}
          setNextPage={setNextPage}
          value={value}
          setValue={setValue}
          defaultColumns={defaultColumns}
          button={button}
          showButton={true}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortType={sortType}
          setSortType={setSortType}
          setLoading={setLoading}
        />
      </div>

      <Modal
        title={editData ? "Edit Room" : "Add New Room"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditData(null); form.resetFields(); setSelectedCampus(null); }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{
          has_projector: true,
          has_whiteboard: true,
          is_active: true,
          is_available_for_booking: true,
          floor: 0,
        }}>
          <Divider orientation="left">Location</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="campus" label="Campus" rules={[{ required: true }]}>
                <Select placeholder="Select Campus" onChange={handleCampusChange} showSearch optionFilterProp="children">
                  {campuses.map((c) => (
                    <Option key={c.id} value={c.id}>{char.toTitleCase(c.name)}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="building" label="Building">
                <Select placeholder="Select Building" showSearch optionFilterProp="children" disabled={!selectedCampus}>
                  {buildings.map((b) => (
                    <Option key={b.id} value={b.id}>{b.code} - {char.toTitleCase(b.name)}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="floor" label="Floor">
                <InputNumber style={{ width: "100%" }} min={-5} max={50} placeholder="0 = Ground" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Room Details</Divider>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="code" label="Room Code" rules={[{ required: true }]}>
                <Input placeholder="e.g., LH-101" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="name" label="Room Name" rules={[{ required: true }]}>
                <Input placeholder="e.g., Lecture Hall 1" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="room_type" label="Room Type" rules={[{ required: true }]}>
                <Select placeholder="Select Type">
                  {ROOM_TYPES.map((t) => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="venue_type" label="Venue Type">
                <Select placeholder="Select" allowClear>
                  {venueTypes.map((v) => (
                    <Option key={v.id} value={v.id}>{char.toTitleCase(v.name)}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="minimum" label="Min Capacity">
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="maximum" label="Max Capacity">
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="exam_capacity" label="Exam Capacity">
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Facilities</Divider>
          <Row gutter={16}>
            <Col span={6}><Form.Item name="has_projector" label="Projector" valuePropName="checked"><Switch /></Form.Item></Col>
            <Col span={6}><Form.Item name="has_whiteboard" label="Whiteboard" valuePropName="checked"><Switch /></Form.Item></Col>
            <Col span={6}><Form.Item name="has_smartboard" label="Smart Board" valuePropName="checked"><Switch /></Form.Item></Col>
            <Col span={6}><Form.Item name="has_air_conditioning" label="Air Conditioning" valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}><Form.Item name="has_computers" label="Has Computers" valuePropName="checked"><Switch /></Form.Item></Col>
            <Col span={6}><Form.Item name="computer_count" label="Computer Count"><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
            <Col span={6}><Form.Item name="has_video_conferencing" label="Video Conf." valuePropName="checked"><Switch /></Form.Item></Col>
            <Col span={6}><Form.Item name="has_audio_system" label="Audio System" valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}><Form.Item name="wheelchair_accessible" label="Wheelchair Access" valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>

          <Divider orientation="left">Status</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_available_for_booking" label="Available for Booking" valuePropName="checked"><Switch /></Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Additional notes" />
          </Form.Item>

          <div style={{ textAlign: "right" }}>
            <Button onClick={() => setModalOpen(false)} style={{ marginRight: 8 }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editData ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};
